// power/card.jsx — the power summary on the home screen.
//
// It answers "is now a good time to run things, and how is today going":
// the price now with a verdict worked out from Amber's forecast, today's
// cost so far (HA's estimate), and the battery. In a price spike the whole
// card fills rust ("needs you"), and it says what HA has done with the
// laundry (power/laundry.js). Ben asked for the full fill 2026-10-26; spikes
// are rare (one spike day in the October history), so it will not wear thin.
//
// The whole card opens the Power page.

import * as React from 'react';
import { fs } from '../direction-a.jsx';
import { FONT, RADIUS } from '../theme.js';
import { fmtMoney, fmtRange } from './model.js';

// A price as the serif number with a small sans unit: "23c", or "$2.85"
// once it reaches a dollar (a spike).
export function Price({ T, cents, size, color }) {
  if (cents == null) return null;
  const dollars = Math.abs(cents) >= 100;
  const unit = { fontFamily: FONT.sans, fontWeight: 400, fontSize: Math.round(size * .42) };
  return (
    <span style={{ fontFamily: FONT.serif, fontSize: size, fontWeight: 500, lineHeight: .9, color: color || T.ink, fontVariantNumeric: 'lining-nums tabular-nums' }}>
      {dollars
        ? <>{cents < 0 ? '-' : ''}<span style={unit}>$</span>{(Math.abs(cents) / 100).toFixed(2)}</>
        : <>{Math.round(cents)}<span style={unit}>c</span></>}
    </span>
  );
}

export function SpikeLine({ T, text, style }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: fs(T, 14), fontWeight: 600, color: T.attention, ...style }}>
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: T.attention, flexShrink: 0 }}/>
      <span>{text}</span>
    </div>
  );
}

// The card's own palette in a spike: rust fill, every text role in the ink
// that reads on it.
function spikeTone(T) {
  return {
    ...T, card: T.attentionFill, cardShadow: 'none',
    ink: T.attentionInk, inkSoft: T.attentionInk, attention: T.attentionInk,
    hair: T.isDark ? 'rgba(22,21,20,.3)' : 'rgba(255,255,255,.35)',
  };
}

// What the card says about the laundry hold, or null.
function laundryNote(laundry) {
  if (!laundry) return null;
  if (laundry.override && laundry.spike) return 'Laundry running anyway.';
  return laundry.line ? `${laundry.line.sub}.` : null;
}

export function PowerCard({ T: base, power, onOpen, compact }) {
  const { price, advice, now, today, ready, laundry } = power;
  const spike = advice?.kind === 'spike';
  const T = spike ? spikeTone(base) : base;
  const shell = {
    display: 'flex', gap: compact ? 16 : 22, width: '100%', height: '100%', boxSizing: 'border-box',
    padding: compact ? '16px 18px' : '18px 22px', alignItems: 'stretch',
    background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
    border: 'none', textAlign: 'left', color: T.ink, fontFamily: FONT.sans,
  };

  if (!ready) {
    return (
      <div style={{ ...shell, flexDirection: 'column', justifyContent: 'center', minHeight: 120 }}>
        <div style={{ fontSize: fs(T, 16), fontWeight: 500 }}>Power prices unavailable</div>
        <div style={{ fontSize: fs(T, 13), color: T.inkSoft }}>Amber's prices aren't reaching Home Assistant.</div>
      </div>
    );
  }

  const note = spike ? laundryNote(laundry) : null;
  // With the laundry sentence above it, the dishwasher line drops its time.
  const sub = spike ? (note ? 'Hold off on the dishwasher too.' : advice.detail)
    : advice?.kind === 'cheap' || advice?.kind === 'flat' ? (advice.dear ? `Dearest ${fmtRange(advice.dear)}` : null)
    : advice?.detail;

  return (
    <button onClick={onOpen} aria-label="Open the power page" style={{ ...shell, cursor: 'pointer' }}>
      <div style={{ flex: '1.2 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {spike
          ? <SpikeLine T={T} text="Price spike"/>
          : <span style={{ fontSize: fs(T, 12), letterSpacing: '.12em', textTransform: 'uppercase', color: T.inkSoft, fontWeight: 500 }}>Power</span>}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <Price T={T} cents={price.cents} size={compact ? 44 : 52}/>
          <span style={{ fontSize: fs(T, 14), color: T.inkSoft }}>per kWh now</span>
        </div>
        {advice && !spike && <div style={{ fontSize: fs(T, 16), fontWeight: 600, lineHeight: 1.25 }}>{advice.headline}</div>}
        {note && <div style={{ fontSize: fs(T, 14), fontWeight: 600, lineHeight: 1.35 }}>{note}</div>}
        {sub && <div style={{ fontSize: fs(T, 14), color: T.inkSoft, lineHeight: 1.35 }}>{sub}</div>}
      </div>
      <div style={{ width: 1, background: T.hair, flexShrink: 0 }}/>
      <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14, fontVariantNumeric: 'tabular-nums' }}>
        {today && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: fs(T, 13), color: T.inkSoft }}>Today so far</span>
            <span style={{ fontSize: fs(T, 22), fontWeight: 600 }}>
              {today.net < 0 ? `Earned ${fmtMoney(-today.net)}` : fmtMoney(today.net)}
            </span>
          </div>
        )}
        {now.battery && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: fs(T, 13), color: T.inkSoft }}>Battery</span>
            <span style={{ fontSize: fs(T, 17), fontWeight: 500 }}>{now.battery.short}</span>
          </div>
        )}
      </div>
    </button>
  );
}
